import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useAnalyticsStatsContextOrDefault } from '~/views/Dashboard/AnalyticsStats/AnalyticsProvider';
import {
  analyticsAuthoringQueryKeyPart,
  analyticsQueryParams,
  type AnalyticsMode,
} from '~/views/Dashboard/AnalyticsStats/analyticsMode';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';

import { analyticsStatCategorySchema } from '../analyticsStatCategory';

const customViewsSchema = z.object({
  userGlobal: z.number(),
  userAccount: z.number(),
  accountExact: z.number(),
  parentExact: z.number(),
  parentChildren: z.number(),
  other: z.number(),
});

export const statDiscoveryEntrySchema = z.object({
  statKey: z.string(),
  statName: z.string(),
  category: analyticsStatCategorySchema,
  description: z.string().optional(),
  hasAppliedLimit: z.boolean(),
  appliedLimitViewPublicId: z.string().nullable(),
  customViews: customViewsSchema,
  assignedTemplateCount: z.number().optional(),
});

const statsListResponseSchema = z.object({
  stats: z.array(statDiscoveryEntrySchema),
});

export type StatDiscoveryEntry = z.infer<typeof statDiscoveryEntrySchema>;
export type AnalyticsStatsListResponse = z.infer<typeof statsListResponseSchema>;

type ResponseData = AnalyticsStatsListResponse | ErrorObjectString;

const getAnalyticsStatsList = async (
  accountId: number,
  accountType: 'client' | 'partner',
  mode?: AnalyticsMode,
  authoringStatSet?: Genie2ReportingMode | null,
): Promise<AnalyticsStatsListResponse> => {
  const resolvedMode = mode ?? 'client';
  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/${accountType}/${accountId}/analytics/stats`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      useNodeEndpoint: true,
      params: analyticsQueryParams(resolvedMode, authoringStatSet),
    },
  );

  if ('error' in data) {
    throw new Error(data.error);
  }

  return statsListResponseSchema.parse(data);
};

export const getAnalyticsStatsListQueryOptions = (
  accountId: number,
  accountType: 'client' | 'partner',
  mode?: AnalyticsMode,
  authoringStatSet?: Genie2ReportingMode | null,
) => {
  const resolvedMode = mode ?? 'client';
  return queryOptions({
    queryKey: [
      'analytics-stats',
      'list',
      accountId,
      accountType,
      resolvedMode,
      analyticsAuthoringQueryKeyPart(resolvedMode, authoringStatSet),
    ],
    queryFn: () => getAnalyticsStatsList(accountId, accountType, resolvedMode, authoringStatSet),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useAnalyticsStatsListQuery = () => {
  const { accountId, accountType, mode, authoringStatSet } = useAnalyticsStatsContextOrDefault();

  return useQuery({
    ...getAnalyticsStatsListQueryOptions(
      accountId,
      accountType,
      mode,
      mode === 'authoring' ? authoringStatSet : undefined,
    ),
    enabled: !!accountId,
    placeholderData: keepPreviousData,
  });
};
